import React, { createContext, useContext, useMemo } from "react";

import { useDashboardWorkloads } from "../../hooks";
import { type DashboardWorkload } from "../../types";
import { Percentile } from "./Components/PercentileSelector";
import { TimeRange } from "./Components/TimeRangeSelector";

export type ClusterDetailDashboardContextType = {
  dashboardWorkloads: DashboardWorkload[];
  loading: boolean;
  timeRange?: TimeRange;
  percentile?: Percentile;
};

export const ClusterDetailDashboardContext = createContext<ClusterDetailDashboardContextType>({
  dashboardWorkloads: [],
  loading: true,
  timeRange: TimeRange.T_30D,
  percentile: Percentile.P_99_9,
});

interface ClusterDetailDashboardContextProviderProps {
  timeRange?: TimeRange;
  percentile?: Percentile;
  clusterId: string;
  children: React.ReactNode;
}

export const ClusterDetailDashboardContextProvider = ({
  children,
  clusterId,
  timeRange,
  percentile,
}: ClusterDetailDashboardContextProviderProps) => {
  const { dashboardWorkloads, loading } = useDashboardWorkloads(clusterId, timeRange);

  const value: ClusterDetailDashboardContextType = useMemo(
    () => ({
      dashboardWorkloads,
      loading,
      timeRange,
      percentile,
    }),
    [dashboardWorkloads, loading, timeRange, percentile]
  );

  return <ClusterDetailDashboardContext.Provider value={value}>{children}</ClusterDetailDashboardContext.Provider>;
};

export const useClusterDetailDashboardContext = () => {
  const context = useContext(ClusterDetailDashboardContext);

  if (!context) {
    throw new Error("useClusterDetailDashboardContext must be used within a ClusterDetailDashboardContextProvider");
  }

  return context;
};
